import { create } from "zustand";

/**
 * The five sync states from the save-model UX spec
 * (architecture/2026-05-21-flow-editor-save-model.md). They escalate
 * with risk: upToDate (calm, recedes) → syncing → offline → error →
 * conflict.
 */
export type SyncState =
  | "upToDate"
  | "syncing"
  | "offline"
  | "error"
  ;
// "conflict" was removed on 14 September 2026. A 409 auto-rebases (fetch the
// server doc, merge by block id, push on the next tick), so the state was
// never reachable — nothing ever set it. If an author should one day be told
// that a colliding same-block edit replaced someone else's, that needs a
// trigger written first; it is not this state coming back by default.

export interface SyncStatus {
  state: SyncState;
  /** When the working copy last reached the server. */
  lastSyncedAt: Date | null;
  /** Edits made since the last successful sync (0 = up to date). */
  pendingCount: number;
  /** The document's server version (optimistic-concurrency token). */
  version: number;
  /** Human-readable detail for the error / conflict states. */
  detail: string | null;
  /**
   * The document these values describe, set once the sync worker has seeded
   * from it. Null while a document is still loading: the values are then the
   * defaults, and a reader must not take `version: 0` for "never saved".
   */
  documentId: string | null;
}

interface SyncStatusStore extends SyncStatus {
  patch: (partial: Partial<SyncStatus>) => void;
  reset: () => void;
}

const INITIAL: SyncStatus = {
  state: "upToDate",
  lastSyncedAt: null,
  pendingCount: 0,
  version: 0,
  detail: null,
  documentId: null,
};

/**
 * Sync status lives in its own store, deliberately separate from
 * `useEditorStore`. The sync indicator subscribes here; the ProseMirror
 * canvas never does — so a completed sync updates the chip without
 * re-rendering the editor. That decoupling is the point: it's what
 * removes the slash-menu collision the old autosave `onSuccess`
 * re-render caused. See architecture/2026-05-21-flow-editor-save-model.md.
 */
export const useSyncStatusStore = create<SyncStatusStore>((set) => ({
  ...INITIAL,
  patch: (partial) => set(partial),
  reset: () => set({ ...INITIAL }),
}));
